import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, useLoadScript, Marker, Autocomplete } from '@react-google-maps/api';
import './LocationPicker.css';

const libraries = ['places'];

const defaultCenter = {
  lat: 31.7683, // Tel Aviv default
  lng: 35.2137
};

const defaultZoom = 10;

function LocationPicker({ locations = [], onChange, onRemove }) {
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(defaultZoom);
  const [searchValue, setSearchValue] = useState('');
  const [newLocation, setNewLocation] = useState({ geolocation: '', meters: 1000 });
  const [isAddingLocation, setIsAddingLocation] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({ geolocation: '', meters: 1000 });
  const [mapsError, setMapsError] = useState(null);
  
  const mapRef = useRef(null);
  const autocompleteRef = useRef(null);
  const searchInputRef = useRef(null);

  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  // Load Google Maps API
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: googleMapsApiKey || '',
    libraries: libraries,
  });

  // Parse geolocation string (format: "lat,lng")
  const parseGeolocation = (geolocation) => {
    if (!geolocation) return null;
    const parts = geolocation.split(',');
    if (parts.length === 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());
      if (!isNaN(lat) && !isNaN(lng)) {
        return { lat, lng };
      }
    }
    return null;
  };

  // Format coordinates to geolocation string
  const formatGeolocation = (lat, lng) => {
    return `${lat},${lng}`;
  };

  // Handle map click to select location
  const handleMapClick = useCallback((event) => {
    if (!isAddingLocation) return;
    
    const lat = event.latLng.lat();
    const lng = event.latLng.lng();
    
    setNewLocation(prev => ({
      ...prev,
      geolocation: formatGeolocation(lat, lng)
    }));
    
    setMapCenter({ lat, lng });
    setMapZoom(15);
  }, [isAddingLocation]);

  // Handle search place selection
  const handlePlaceSelect = useCallback(() => {
    if (!autocompleteRef.current) return;
    
    const place = autocompleteRef.current.getPlace();
    if (place && place.geometry) {
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      
      setNewLocation(prev => ({
        ...prev,
        geolocation: formatGeolocation(lat, lng)
      }));
      
      setMapCenter({ lat, lng });
      setMapZoom(15);
      setSearchValue(place.formatted_address || '');
    }
  }, []);

  // Handle adding new location
  const handleAddLocation = () => {
    if (!newLocation.geolocation || !newLocation.meters) {
      alert('יש להזין מיקום ומרחק');
      return;
    }

    const locationData = {
      geolocation: newLocation.geolocation,
      meters: parseInt(newLocation.meters) || 1000
    };

    onChange([...locations, locationData]);
    setNewLocation({ geolocation: '', meters: 1000 });
    setSearchValue('');
    setIsAddingLocation(false);
    if (autocompleteRef.current) {
      autocompleteRef.current.set('place', null);
    }
  };

  // Handle removing location
  const handleRemoveLocation = (index) => {
    if (window.confirm('האם אתה בטוח שברצונך למחוק מיקום זה?')) {
      const newLocations = locations.filter((_, i) => i !== index);
      onChange(newLocations);
      if (editingIndex === index) setEditingIndex(null);
      else if (editingIndex !== null && editingIndex > index) setEditingIndex(editingIndex - 1);
    }
  };

  // Start editing a saved location
  const handleStartEdit = (index) => {
    setEditingIndex(index);
    setEditForm({
      geolocation: locations[index].geolocation,
      meters: locations[index].meters
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingIndex(null);
  };

  // Save edited location
  const handleSaveEdit = () => {
    if (!editForm.geolocation || !editForm.meters) {
      alert('יש להזין מיקום ומרחק');
      return;
    }
    const updated = locations.map((loc, i) =>
      i === editingIndex
        ? { geolocation: editForm.geolocation.trim(), meters: parseInt(editForm.meters) || 1000 }
        : loc
    );
    onChange(updated);
    setEditingIndex(null);
  };

  // Center map on existing locations or on new location when typing valid coords
  useEffect(() => {
    if (locations.length > 0 && !isAddingLocation && editingIndex === null) {
      const firstLocation = parseGeolocation(locations[0].geolocation);
      if (firstLocation) {
        setMapCenter(firstLocation);
        setMapZoom(12);
      }
    }
  }, [locations, isAddingLocation, editingIndex]);

  // When adding: if user types valid lat,lng, center map on it
  useEffect(() => {
    if (isAddingLocation && newLocation.geolocation) {
      const coords = parseGeolocation(newLocation.geolocation);
      if (coords) {
        setMapCenter(coords);
        setMapZoom(15);
      }
    }
  }, [isAddingLocation, newLocation.geolocation]);

  // Handle Google Maps API errors
  useEffect(() => {
    if (loadError) {
      console.error('[LocationPicker] Google Maps API Error:', loadError);
      let errorMessage = 'שגיאה בטעינת Google Maps';
      
      const errorStr = loadError.toString();
      if (errorStr.includes('ApiNotActivatedMapError') || errorStr.includes('ApiNotActivated')) {
        errorMessage = 'Maps JavaScript API לא מופעל';
      } else if (errorStr.includes('RefererNotAllowedMapError')) {
        errorMessage = 'הדומיין לא מורשה להשתמש ב-API Key';
      } else if (errorStr.includes('InvalidKeyMapError')) {
        errorMessage = 'API Key לא תקין';
      }
      
      setMapsError(errorMessage);
    } else {
      setMapsError(null);
    }
  }, [loadError]);

  if (!googleMapsApiKey) {
    return (
      <div className="location-picker-error">
        <p>⚠️ Google Maps API Key לא מוגדר</p>
        <p>אנא הגדר את REACT_APP_GOOGLE_MAPS_API_KEY ב-environment variables</p>
      </div>
    );
  }

  if (mapsError) {
    return (
      <div className="location-picker-error">
        <p>⚠️ {mapsError}</p>
        <div style={{ marginTop: '10px', fontSize: '14px', textAlign: 'right' }}>
          <p><strong>פתרון:</strong></p>
          <ol style={{ textAlign: 'right', paddingRight: '20px' }}>
            <li>לך ל-<a href="https://console.cloud.google.com/apis/library" target="_blank" rel="noopener noreferrer">Google Cloud Console</a></li>
            <li>בחר את הפרויקט: <code>gsradio-androidtv-384715</code></li>
            <li>הפעל את ה-APIs הבאים:
              <ul>
                <li><strong>Maps JavaScript API</strong></li>
                <li><strong>Places API</strong> (או Places API (New))</li>
              </ul>
            </li>
            <li>ודא שה-API Key קשור לפרויקט הנכון</li>
            <li>אם ה-API Key מוגבל, הוסף את הדומיין שלך לרשימת ה-Referrers המורשים</li>
          </ol>
          <button 
            type="button" 
            onClick={() => setMapsError(null)}
            style={{ marginTop: '10px', padding: '5px 10px' }}
          >
            נסה שוב
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="location-picker">
      <div className="location-picker-header">
        <h4>מיקומי הפרסומת</h4>
        <button
          type="button"
          className="location-picker-add-button"
          onClick={() => setIsAddingLocation(!isAddingLocation)}
        >
          {isAddingLocation ? 'ביטול' : '+ הוסף מיקום'}
        </button>
      </div>

      {isAddingLocation && (
        <div className="location-picker-add-form">
          <div className="location-picker-map-container">
            {!isLoaded ? (
              <div style={{ padding: '20px', textAlign: 'center' }}>
                <p>טוען מפות...</p>
              </div>
            ) : (
              <>
                <div className="location-picker-search">
                  <Autocomplete
                    onLoad={(autocomplete) => {
                      autocompleteRef.current = autocomplete;
                    }}
                    onPlaceChanged={handlePlaceSelect}
                  >
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="חפש כתובת או מקום..."
                      value={searchValue}
                      onChange={(e) => setSearchValue(e.target.value)}
                      className="location-picker-search-input"
                    />
                  </Autocomplete>
                </div>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '400px' }}
                  center={mapCenter}
                  zoom={mapZoom}
                  onClick={handleMapClick}
                  onLoad={(map) => {
                    mapRef.current = map;
                  }}
                  options={{
                    streetViewControl: false,
                    mapTypeControl: true,
                    fullscreenControl: true,
                    zoomControl: true,
                  }}
                >
                  {/* Show existing locations */}
                  {locations.map((location, index) => {
                    const coords = parseGeolocation(location.geolocation);
                    if (!coords) return null;
                    
                    return (
                      <Marker
                        key={index}
                        position={coords}
                        label={`${index + 1}`}
                        title={`מיקום ${index + 1}: ${location.meters}m`}
                      />
                    );
                  })}

                  {/* Show new location marker if selected */}
                  {(() => {
                    if (!newLocation.geolocation) return null;
                    const coords = parseGeolocation(newLocation.geolocation);
                    if (!coords) return null;
                    
                    return (
                      <Marker
                        key="new-location"
                        position={coords}
                        icon={{
                          path: window.google?.maps?.SymbolPath?.CIRCLE || 0,
                          scale: 8,
                          fillColor: '#FF0000',
                          fillOpacity: 1,
                          strokeColor: '#FFFFFF',
                          strokeWeight: 2,
                        }}
                        title="מיקום חדש"
                      />
                    );
                  })()}
                </GoogleMap>
                <div className="location-picker-instructions">
                  <p>💡 לחץ על המפה כדי לבחור מיקום, או השתמש בחיפוש למעלה</p>
                </div>
              </>
            )}
          </div>

          <div className="location-picker-form-fields">
            <div className="location-picker-field">
              <label>מיקום (lat,lng):</label>
              <input
                type="text"
                value={newLocation.geolocation}
                onChange={(e) => setNewLocation(prev => ({ ...prev, geolocation: e.target.value }))}
                placeholder="31.7683,35.2137"
                className="location-picker-geolocation-input"
              />
            </div>

            <div className="location-picker-field">
              <label>מרחק (מטרים):</label>
              <input
                type="number"
                value={newLocation.meters}
                onChange={(e) => setNewLocation(prev => ({ ...prev, meters: parseInt(e.target.value) || 1000 }))}
                min="100"
                step="100"
                placeholder="1000"
              />
            </div>

            <button
              type="button"
              className="location-picker-save-button"
              onClick={handleAddLocation}
              disabled={!newLocation.geolocation || !newLocation.meters}
            >
              שמור מיקום
            </button>
          </div>
        </div>
      )}

      {/* List of existing locations */}
      {locations.length > 0 && (
        <div className="location-picker-list">
          <h5>מיקומים שמורים ({locations.length}):</h5>
          {locations.map((location, index) => {
            const isEditing = editingIndex === index;
            if (isEditing) {
              return (
                <div key={index} className="location-picker-item location-picker-item-editing">
                  <div className="location-picker-edit-fields">
                    <div className="location-picker-field">
                      <label>מיקום (lat,lng):</label>
                      <input
                        type="text"
                        value={editForm.geolocation}
                        onChange={(e) => setEditForm(prev => ({ ...prev, geolocation: e.target.value }))}
                        placeholder="31.7683,35.2137"
                        className="location-picker-geolocation-input"
                      />
                    </div>
                    <div className="location-picker-field">
                      <label>מרחק (מטרים):</label>
                      <input
                        type="number"
                        value={editForm.meters}
                        onChange={(e) => setEditForm(prev => ({ ...prev, meters: parseInt(e.target.value) || 1000 }))}
                        min="100"
                        step="100"
                      />
                    </div>
                  </div>
                  <div className="location-picker-item-actions">
                    <button
                      type="button"
                      className="location-picker-save-button"
                      onClick={handleSaveEdit}
                      disabled={!editForm.geolocation || !editForm.meters}
                    >
                      שמור
                    </button>
                    <button
                      type="button"
                      className="location-picker-cancel-button"
                      onClick={handleCancelEdit}
                    >
                      ביטול
                    </button>
                  </div>
                </div>
              );
            }
            return (
              <div key={index} className="location-picker-item">
                <div className="location-picker-item-info">
                  <span className="location-picker-item-number">{index + 1}.</span>
                  <div className="location-picker-item-details">
                    <div className="location-picker-item-geolocation">
                      {location.geolocation}
                    </div>
                    <div className="location-picker-item-meters">
                      מרחק: {location.meters.toLocaleString()} מטרים
                    </div>
                  </div>
                </div>
                <div className="location-picker-item-buttons">
                  <button
                    type="button"
                    className="location-picker-item-edit"
                    onClick={() => handleStartEdit(index)}
                    title="ערוך מיקום"
                  >
                    ✏️
                  </button>
                  <button
                    type="button"
                    className="location-picker-item-remove"
                    onClick={() => handleRemoveLocation(index)}
                    title="מחק מיקום"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {locations.length === 0 && !isAddingLocation && (
        <div className="location-picker-empty">
          <p>אין מיקומים מוגדרים. לחץ על "הוסף מיקום" כדי להתחיל.</p>
        </div>
      )}
    </div>
  );
}

export default LocationPicker;

